import { useContext, useEffect, useRef, useState } from 'react'
import { useFormikContext, Field, ErrorMessage } from 'formik'
import { AnimatePresence, motion } from "framer-motion"
import classnames from 'classnames/bind'
import { emitCustomEvent } from 'react-custom-events'
import * as Yup from 'yup'
import { StoreContext } from '~/store'
import { 
  FormContent, FormHeader, BookingSummary, ButtonDrawer, DefautButton, CheckboxField, RadioField, 
  FieldButton, OptionsPanel, FieldDropDown, InputField, FormError, Modal, Notifications,
  AddVoucher, AddAccount, PaymentTypes, BookingSummaryField, EditField, Icon, TitleInfo, CloseButton
} 
from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { wrapWithValidation } from '~/utils'
import { createBooking, prepareBooking } from '~/lib/api'
import { showErrorModal } from '~/utils'
import { getVoucherValue } from '~/utils/data'
import { usePrevious } from '~/utils/hooks'
import * as MotionElement from '~/components/ui/MotionElement'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as panelStyles from '~/components/ui/OptionsPanel/index.module.css'
import { useTranslation, Trans } from 'react-i18next'
import i18next from '~/i18n'
import { formatBookingRequest, formatReadableDateOnly } from '../../../utils/format'
import { track } from '../../../utils/gtm'
import AddMessage from '../../ui/AddMessage'

const getPaymentText = (values) => {
  if (values.payment.indexOf('Account') !== -1) {
    return values?.account?.accountName
  }
  else if (values.payment.indexOf('CreditCard') !== -1) {
    return values?.account?.maskedAccount
  }
  return i18next.t('title-pay-in-car')
}

const renderVoucherInfo = voucher => {
  let str = i18next.t('expires-at', { date: formatReadableDateOnly(new Date(voucher.expiry)) })
  if (voucher.valueType == 'PERCENTAGE') {
    return <>{str}<br />{i18next.t('info-voucher-max', { amount: voucher.maxPriceToBeDiscounted })}</>
  }
  return str
}

const cx = classnames.bind(common)
const cxPanel = classnames.bind(panelStyles)

const ConfirmBooking = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const paymentButtonRef = useRef()
  const payInCarRef = useRef()
  const optionsButtonRef = useRef()
  const messageFieldRef = useRef()
  const nameFieldRef = useRef()
  const [isPaymentOpen, setIsPaymentOpen] = useState(false)
  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false)
  const [isAddVoucherOpen, setIsAddVoucherOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPaymentModuleOpen, setIsPaymentModuleOpen] = useState(false)
  
  const showPaymentModule = () => {
    setIsPaymentModuleOpen(true);
  }

  const hidePaymentModule = () => {
    setIsPaymentModuleOpen(false);
  }

  const { prev, next } = useContext(StepperContext)
  const { config, user, paymentAccounts, refreshUser, setPreferredPaymentAccount, getActiveBookings } = useContext(StoreContext)

  const { values, setFieldValue } = useFormikContext()

  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice

  const disableCreditCards = !values.priceType?.paymentMethods?.includes('CreditCard')
  const disableTSABAccounts = !values.priceType?.paymentMethods?.includes('TSABAccount')
  const disableTCAAccounts = !values.priceType?.paymentMethods?.includes('TravelClearingAccount')
  const disableVouchers = !values.priceType?.voucherEnabled;

  const prevAccounts = usePrevious(user?.accounts)
  const prevPaymentAccounts = usePrevious(paymentAccounts)
  const prevVouchers = usePrevious(user?.vouchers)

  const hasAccounts = user?.accounts?.length > 0
  const hasCreditCards = paymentAccounts?.length > 0
  const hasVouchers = user?.vouchers?.length > 0

  const showReference = values?.company !== false || values?.account?.type === 'TSAB' ||  values?.account?.type === 'TCA'
  const needReference = values?.company?.requireReference === true || 
        (values?.account !== false && (values?.account.type ==='TSAB' || values?.account.type ==='TCA') && values?.account.flags.includes('reference'))

  const handleBookingComplete = (response) => {
    setIsLoading(false)
    getActiveBookings()
    refreshUser()
    sendToGTM(values, response.bookingId)
    setPreferredPaymentAccount(values.account)
    setFieldValue('bookingId', response.bookingId, false)
    next()
  }

  const makeRequest = () => {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const bookingFunc = isCreditCard ? prepareBooking : createBooking
    console.log("mkre request");
    setIsLoading(true)
    bookingFunc(values, user.userId).then((response) => {
      console.log("start booking");
      console.log(response)
      if (!isNaN(response?.bookingId)) {
        if (isCreditCard) {
          emitCustomEvent(
            'CASHIER_PAY_BOOKING', 
            {
              bookingId: response.bookingId,
              accountId: values.account.accountId, 
              success: () => handleBookingComplete(response),
              doneLoading: () => setIsLoading(false)
            }
          )
        }
        else {
          handleBookingComplete(response)
        }
      }
    })
    .catch((error) => {
      console.log(error);
      console.log("error on booking");
      if (error?.response?.error === 'INVALID_PHONE_NUMBER') {
        setFieldError('phone', error?.response?.message)
      }
      else if (!(error?.response?.status === 401 && error?.response?.error === 'Unauthorized')) {
        console.error(error)
        showErrorModal(error?.response?.message)
      }
      setIsLoading(false)
    })
    // .finally(() => {
    //   setIsLoading(false)
    // })
  }

  const wrappedPrev = () => {
    prev('how')
  }

  const sendToGTM = (values, bookingId) => {
    const booking = formatBookingRequest(values);
    const priceComponents = fixedPrice ? values.priceEstimate?.prices?.filter((value) => {return value.serviceType === booking.serviceType}) : false;
    const prices = priceComponents && priceComponents.length > 0 ? priceComponents[0].priceComponents?.filter((value) => {return value.type === 'fixedPrice'}) : false;
    const price = prices && prices.length > 0 ? prices[0].amount : false;
    const trackData = {
      price: price,
      preBook: values.pickupTime ? true : false,
      pickupTime: booking.pickUpTimeStamp,
      bookingNumber: bookingId,
      carType: booking.serviceType,
      extras: booking.vehicleOptions,
      fixedPrice: fixedPrice && booking.priceToken ? true : false
    };
    track('confirmBooking', trackData);
  }

  const wrappedNext = wrapWithValidation(makeRequest, useFormikContext())

  const setAccount = account => {
    if (!account) {
      setFieldValue('accountReference', '', false)
    }
    setFieldValue('account', account, false)
  }

  const openOptions = () => {
    setIsOptionsOpen(true)
    setTimeout(() => {
      if (messageFieldRef.current) messageFieldRef.current.focus()
    }, 200)
  }

  const closeOptions = () => {
    setIsOptionsOpen(false)
    if (optionsButtonRef.current) optionsButtonRef.current.focus()
  }

  const openPayment = () => {
    setIsPaymentOpen(true)
    setTimeout(() => {
      if (payInCarRef.current) payInCarRef.current.focus()
    }, 200)
  }

  const closePayment = () => {
    setIsPaymentOpen(false)
    if (paymentButtonRef.current) paymentButtonRef.current.focus()
  }

  const addCard = () => {
    closeOptions()
    emitCustomEvent('CASHIER_ADD_CARD', { success: refreshUser })
  }

  const openAddAccount = () => {
    setIsAddAccountOpen(true)
  }

  const closeAddAccount = () => {
    setIsAddAccountOpen(false)
  }

  const openAddVoucher = voucher => {
    setIsAddVoucherOpen(true)
  }

  const closeAddVoucher = () => {
    setIsAddVoucherOpen(false)
  }

  const referenceFieldRef = useRef()
  const [referenceIsOpen, setReferenceIsOpen] = useState(false);
  const toggleReference = () => {
    setReferenceIsOpen(!referenceIsOpen)
  }

  const buttons = [
    { func: wrappedPrev, isBack: true },
    { func: wrappedNext, title: t('button-book-now'), isLoading },
  ]

  useEffect(() => {
    if (user?.accounts?.length > prevAccounts?.length) {
      const addedAccount = user?.accounts?.find(a => {
        return (prevAccounts.find(b => b.accountNumber === a.accountNumber) === undefined) ? true : false
      })
      if (addedAccount !== undefined) {
        setFieldValue('payment', `Account-${addedAccount.accountNumber}`, false)
        setAccount(addedAccount)
      }
    }
  }, [user?.accounts])

  useEffect(() => {
    if (paymentAccounts?.length > prevPaymentAccounts?.length) {
      const addedAccount = paymentAccounts.find(a => {
        return (prevPaymentAccounts.find(b => b.accountId === a.accountId) === undefined) ? true : false
      })
      if (addedAccount !== undefined) {
        setFieldValue('payment', `CreditCard-${addedAccount.accountId}`, false)
        setAccount(addedAccount)
      }
    }
  }, [paymentAccounts])

  useEffect(() => {
    if (user?.vouchers?.length > prevVouchers?.length) {
      const addedVoucher = user?.vouchers?.find(a => {
        return (prevVouchers.find(b => b.voucherCode === a.voucherCode) === undefined) ? true : false
      })
      if (addedVoucher !== undefined) {
        if(!disableVouchers) setFieldValue('vouchers', [addedVoucher.voucherCode], false)
      }
    }
  }, [user?.vouchers])

  useEffect(() => {
    if (nameFieldRef.current) nameFieldRef.current.focus()
  }, [nameFieldRef])
  
  const contentOptions = true;

  return (
    <div layout className={cx(common.widgetWrapper, { isLoading, fullscreen })}>
      <FormContent>
        <FormHeader onlyFullscreen>{t('your-trip')}</FormHeader>
        <FormHeader onlyMobile>{t('header-confirm-your-booking')}</FormHeader>
        <Notifications fullscreen={fullscreen}/>
        <BookingSummary values={values} showHow showPayment currentView={currentView} />
      </FormContent>
      <MotionElement.div className={common.contentBottom}>
        <FormHeader onlyFullscreen>{t('header-confirm-your-booking')}</FormHeader>
        
            <div className={cxPanel(panelStyles.title, {noTopMargin:true})}>{t('title-payment')}</div>
            <Field type="radio" icon="PayInCar" name="payment" title={t('title-pay-in-car')} value="PayInCar" onClick={()=>setAccount(false)} innerRef={payInCarRef} component={RadioField} />
            {paymentAccounts?.map((account, index) => (
              <Field 
                key={index} 
                type="radio" 
                name="payment" 
                icon={`Card${account.cardType}`} 
                title={account.maskedAccount} 
                info={account.cardType} 
                value={`CreditCard-${account.accountId}`} 
                onClick={()=>setAccount(account)} 
                tabIndex={isPaymentOpen ? 0 : -1} 
                disabled={disableCreditCards } 
                disabledDescription={
                  disableCreditCards ? "Inte tillgängligt" : null
                }
                component={RadioField} 
              />
            ))}
            {user?.accounts?.map((account, index) => {
              const disableNoDestination = account?.flags?.includes('destination') && values?.destination==="";
              return (
                <Field 
                  key={index} 
                  type="radio" 
                  name="payment" 
                  icon="TSAB"
                  title={account.accountName} 
                  info={account.accountNumber} 
                  value={`Account-${account.accountNumber}`} 
                  onClick={()=>setAccount(account)} 
                  tabIndex={isPaymentOpen ? 0 : -1}
                  disabledDescription={
                    disableNoDestination ? 'Destination krävs' :
                    ((account.type === 'TSAB' && disableTSABAccounts ) 
                    || (account.type === 'TCA' && disableTCAAccounts)) ? "Inte tillgängligt" : null
                  }
                  disabled={
                    disableNoDestination ||
                    (account.type === 'TSAB' && disableTSABAccounts ) 
                    || (account.type === 'TCA' && disableTCAAccounts)
                  }
                  component={RadioField} 
                />
            )})}
            {showReference && <>
              <BookingSummaryField 
                title={t('placeholder-reference')} 
                value={(!values.accountReference || values.accountReference.trim()==='') ? (needReference ? t('validation-need-reference') : '') : values.accountReference}
                icon="Reference" changeToggle={toggleReference} 
                required
                empty={(!values.accountReference || values.accountReference.trim()==='') && needReference ? true : false}>
              </BookingSummaryField>
              <Modal isOpen={referenceIsOpen} onClose={toggleReference}>
                <EditField fields={[{id:"accountReference", title:t('placeholder-reference')}]} title={t('placeholder-reference')} innerRef={referenceFieldRef} onClose={toggleReference} />
              </Modal>
              <div className={common.hidden}>
                <Field name="accountReference" placeholder={t('placeholder-reference')} maxLength="40" component={InputField} />
              </div>
            </>}
            <div className={cx(styles.addButton, common.fullWidth)}>
              <DefautButton isSmall onClick={showPaymentModule} tabIndex={isPaymentOpen ? 0 : -1} title={`+ ${t('button-add-payment')}`}/>
              {/* <DefautButton isSmall onClick={openAddAccount} tabIndex={isPaymentOpen ? 0 : -1} title={`+ ${t('button-add-account')}`}/> */}
            </div>
            {/* 
            <div className={cx(common.fullWidth)}>
              <DefautButton isSmall onClick={openAddVoucher} tabIndex={isPaymentOpen ? 0 : -1} title={`+ ${t('button-add-new-payment')}`}/>
            </div>
            */}
            <AnimatePresence>
              {isPaymentModuleOpen && <motion.div initial={{ opacity: 0 }} exit={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles.dimmer} onClick={hidePaymentModule}></motion.div>}
              {isPaymentModuleOpen && 
                <motion.div 
                  transition={{ duration: 0.15, ease: "circOut" }}
                  style={{originY: 0}} 
                  initial={{ scaleY: 0, height: 'auto' }} 
                  exit={{ scaleY: 0, height:0 }} 
                  animate={{ scaleY: 1 }} 
                  className={styles.addPaymentPanel}
                >
                  <div className={styles.addPaymentPanelTitle}>Lägg till betalmetod</div> 
                  <CloseButton onClick={hidePaymentModule} small/>
                    <div className={styles.addPaymentItem} onClick={addCard}>
                      <div className={styles.addPaymentItemTitle}>
                        <img src={`/app/themes/taxi-stockholm/static/img/option-icons/PayInCar.png`} className={styles.icon} />
                        <TitleInfo title="Betalkort" />
                      </div>
                      <Icon id="plus" />
                    </div>
                    <div className={styles.addPaymentItem} onClick={openAddAccount}>
                      <div className={styles.addPaymentItemTitle}>
                        <img src={`/app/themes/taxi-stockholm/static/img/option-icons/TSAB.png`} className={styles.icon} />
                        <TitleInfo title="Konto" />
                      </div>
                      <Icon id="plus" />
                    </div>
                </motion.div>
              }
            </AnimatePresence>
            <Modal onClose={closeAddAccount} isPanel isOpen={isAddAccountOpen}>
              <AddAccount onComplete={closeAddAccount} />
            </Modal>
            <div className={styles.vouchersPanel}>
              <div className={panelStyles.title}>{t('title-vouchers')}</div>
              
              {!hasVouchers && <div className={styles.instructions}>{t('info-no-vouchers')}</div>}
              {user?.vouchers?.map((voucher, index) => (
                <Field 
                  key={index} 
                  name="vouchers" 
                  icon="DigitalVoucher"
                  value={voucher.voucherCode} 
                  title={`${voucher.voucherCode} - ${getVoucherValue(voucher)}`}
                  info={renderVoucherInfo(voucher)}
                  disabled={disableVouchers || (values?.vouchers?.length > 0 && !values?.vouchers?.includes(voucher.voucherCode))} 
                  disabledDescription={
                    disableVouchers || (values?.vouchers?.length > 0 && !values?.vouchers?.includes(voucher.voucherCode)) ? "Inte tillgängligt" : null
                  }
                  noPadding
                  tabIndex={isPaymentOpen ? 0 : -1}
                  component={CheckboxField} 
                />
              ))}
              <div className={cx(styles.addButton, common.fullWidth)}>
                <DefautButton isSmall onClick={openAddVoucher} tabIndex={isPaymentOpen ? 0 : -1} title={`+ ${t('button-add-new-voucher')}`}/>
              </div>
              <Modal onClose={closeAddVoucher} isPanel isOpen={isAddVoucherOpen}>
                <AddVoucher onComplete={closeAddVoucher} />
              </Modal>
              {/* {isAddVoucherOpen && <Modal onClose={closeAddVoucher} isPanel isOpen>
                <AddVoucher onComplete={closeAddVoucher} />
              </Modal>} */}
              {hasVouchers && <div className={cx(styles.instructions, common.fullWidth)}>
                <br />
                <Trans 
                  i18nKey={disableVouchers ? 'info-voucher-codes-partner' : 'info-voucher-codes'}
                  components={{
                    link1: <a href={config.paymentTermsLink} target="_blank"/>
                  }}
                />
            </div>}
          </div>
          {/* 
          <div className={panelStyles.title}>VEM</div>
          <Field name="name" placeholder={t('placeholder-name')} innerRef={nameFieldRef} component={InputField} border />
          <Field name="phone" placeholder={t('placeholder-phone')} component={InputField} border />
          <div className={cx(common.fullWidth, styles.info)}>
          <Trans 
            i18nKey="info-accept-booking-terms" 
            components={{
              link1: <a href={config.termsLink} target="_blank"/>
            }}
          />
        </div>
          
          <FieldButton title={t('title-payment')} innerRef={paymentButtonRef} onClick={openPayment} alternateBackground>
            {!disableVouchers && values.vouchers.length === 0 && user?.vouchers?.length > 0 && <div className={cx(common.option)}>{t('info-num-vouchers', { count: user.vouchers.length })}</div>}
          </FieldButton>
          <FieldButton title={t('title-message-to-driver')} innerRef={optionsButtonRef} onClick={openOptions} alternateBackground />
          {showReference && <div className={common.fullWidth}>
            <Field name="accountReference" placeholder={t('placeholder-reference')} maxLength="40" component={InputField} alternateBackground />
          </div>}
          
          <FieldButton title={t('title-message-to-driver')} innerRef={optionsButtonRef} onClick={openOptions} alternateBackground />
          {values?.generalComment && <div className={cx(common.fullWidth, styles.message)}>
          <div className={common.label}>{t('title-message-to-driver')}</div>
          <div className={common.title}>{values.generalComment}</div>
        </div>}
        <div className={cx(common.fullWidth, styles.info)}>
          <Trans 
            i18nKey="info-accept-booking-terms" 
            components={{
              link1: <a href={config.termsLink} target="_blank"/>
            }}
          />
        </div>
          {isAddAccountOpen && <Modal onClose={closeAddAccount} isOpen={isAddAccountOpen}>
            <AddAccount onComplete={closeAddAccount} />
          </Modal>}
          <Modal isOpen={isOptionsOpen} onClose={closeOptions}>
            <AddMessage onClose={closeOptions} />
          </Modal>*/}
          
        
      </MotionElement.div>
      <ButtonDrawer buttons={buttons} fullscreen={fullscreen}/>
     
      <PaymentTypes></PaymentTypes>
    </div>
  )
}

ConfirmBooking.validationSchema = Yup.object({
  name: Yup.string().required(i18next.t('validation-need-name')),
  phone: Yup.string()
    .matches(/^\+?\d+$/, i18next.t('validation-only-numbers'))
    .required(i18next.t('validation-need-phone')),
  accountReference: Yup.string()
    .when(['account', 'company'], (account, company) => {
      const needReference = company?.requireReference === true || 
        (account !== false && (account.type ==='TSAB' || account.type ==='TCA') && account.flags.includes('reference'))
      return needReference ? Yup.string().required(i18next.t('validation-need-reference')) : Yup.string()
    }),
})

export default ConfirmBooking
